/**
 * BrandContext — hostname-based brand theming.
 *
 * On mount, calls /api/brands/current and stores the brand config in
 * context. Components can do `const brand = useBrand()` and read:
 *   - brand.id           ("toprankpulse" | "supermajstri" | "stavebne_dopyty")
 *   - brand.name         display name
 *   - brand.primary_color / accent_color
 *   - brand.tagline_sk / tagline_en
 *   - brand.support_email
 *
 * Multi-brand UI components (LandingHeader, Footer, etc.) read from
 * this context to swap logo / colours / tagline without re-deploying
 * the build.
 *
 * Sane default while loading: toprankpulse colors.
 */
import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { API } from "../config";

const DEFAULT_BRAND = {
  id: "toprankpulse",
  name: "TopRankPulse",
  domain: "toprankpulse.com",
  primary_color: "#FFB800",
  accent_color: "#10B981",
  tagline_sk: "EU-native multi-agent SaaS pre e-shopy",
  tagline_en: "EU-native multi-agent SaaS for e-shops",
  logo_text: "TopRankPulse",
  support_email: "support@toprankpulse.com",
  default_lang: "sk",
  tracker_enabled: true,
};

const BrandContext = createContext(DEFAULT_BRAND);

export function BrandProvider({ children }) {
  const [brand, setBrand] = useState(DEFAULT_BRAND);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/brands/current`, {
          params: { host: window.location.hostname },
        });
        if (!cancelled && r.data && r.data.id) setBrand(r.data);
      } catch (e) {
        // Fail silent — keep default brand
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <BrandContext.Provider value={brand}>{children}</BrandContext.Provider>
  );
}

export const useBrand = () => useContext(BrandContext);
